import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Award } from "assets/images/svgs/award.svg";
import { ReactComponent as AwardSecond } from "assets/images/svgs/award-second.svg";
import { ReactComponent as AwardLink } from "assets/images/svgs/award-link.svg";
import { ReactComponent as ExperienceChart } from "assets/images/svgs/experience-chart.svg";
import { ReactComponent as SheildTick } from "assets/images/svgs/shield-tick.svg";
import { ReactComponent as EditTwo } from "assets/images/svgs/edit-2.svg";
import { ReactComponent as FavoriteChart } from "assets/images/svgs/favorite-chart.svg";
import { ReactComponent as FolderOpen } from "assets/images/svgs/folder-open.svg";
import { ReactComponent as Verify } from "assets/images/svgs/verify.svg";
import { ReactComponent as SecurityUser } from "assets/images/svgs/security-user.svg";
import { ReactComponent as QuoteCircle } from "assets/images/svgs/quote-down-circle.svg";
import { ReactComponent as Setting } from "assets/images/svgs/setting-4.svg";
import { ReactComponent as Logout } from "assets/images/svgs/logout.svg";
import popupImg from "assets/images/others/host-side-images/popupImg.png";
import profileGif from "assets/images/gifs/add.gif";
import { useSelector } from "react-redux";
import { IoIosCloseCircleOutline } from "react-icons/io";
import useOutsideClick from "hooks/click-outside";
import { siteRoutes } from "utils/helpers/enums/routes";
import CustomerSupport from "../CustomerSupport";
import useStore from "hooks/useStore";
import ReviewsRatingIcon from "assets/images/svgs/reviewStarSvg.svg";
import { CommunityIcon, CommunityIconHost, EventsBadgeSvg, ManagePaymentsSvg, SupportHost, TicketExpiredIcon } from "assets/images/svgs";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const NavbarDrawer: React.FC<DrawerProps> = ({ isOpen, onClose }) => {
  const modalRef = useOutsideClick(() => {
  });
  const [profileCompleted, setProfileCompleted] = useState<boolean | null>(
    null
  );
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);
  const [showLaunchingSoonModal, setShowLaunchingSoonModal] = useState(false);
  const [showSupportDrawer, setShowSupportDrawer] = useState(false);

  const { getUserProfilePic, getUserFullName } = useStore();
  const userProfilePic = getUserProfilePic();
  const userFullName = getUserFullName();

  const navigate = useNavigate();

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  // Function to get border color based on subscription tier
  const getSubscriptionBorderColor = (subscriptionType: string) => {
    switch (subscriptionType) {
      case 'TIER_1':
        return 'border-[3px] border-white';
      case 'TIER_2':
        return 'border-[3px] border-gray-400'; // Silver
      case 'TIER_3':
        return 'border-[3px] border-yellow-400'; // Golden
      default:
        return 'border-[3px] border-white'; // Default
    }
  };

  useEffect(() => {
    if (registeredData) {
      const completed = registeredData?.profiles?.host?.profileCompleted;
      setProfileCompleted(completed);
    }
  }, [registeredData]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);
  const handleNavigation = (item: any) => {
    if (item.label === "Logout") {
      localStorage.clear();
      onClose();
      navigate(item.link);
      return;
    }

    if (item.label === "Edit Profile") {
      if (profileCompleted === false || profileCompleted === null) {
        onClose();
        navigate("/host-profile");
        return;
      } else {
        onClose();
        navigate("/edit-profile");
        return;
      }
    }

    if (
      item.label === "Subscription" ||
      item.label === "General Settings" ||
      item.label === "My Badges" ||
      item.label === "My Ratings" ||
      item.label === "Resource Library" ||
      item.label === "Manage Payments" ||
      item.label === "Event Badges" ||
      item.label === "My Representatives" ||
      item.label === "My Experiences" ||
      item.label === "My Cancelled Tickets" 
    ) {
      if (profileCompleted === false || profileCompleted === null) {
        setShowCompleteProfileModal(true);
      } else {
        onClose();
        navigate(item.link);
      }
      return;
    }

    if (item.label === "Customer Support") {
      setShowSupportDrawer(true);
      return;
    }

    onClose();
    navigate(item.link);
  };

  return (
    <Fragment>
      <div
        className={`fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        style={{ zIndex: 'calc(var(--z-drawer) + 1)' }}
        onClick={onClose}
      >
        <div
          className={`fixed top-0 right-0 h-full w-4/5 sm:w-1/2 md:w-1/3 lg:w-[480px] overflow-y-auto bg-white p-4 md:p-6 lg:p-10 shadow-lg transition-transform duration-300 ease-in-out transform ${isOpen ? "translate-x-0" : "translate-x-full"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-2xl text-gray-600 hover:text-black"
          >
            &times;
          </button>

          <div className="flex items-center space-x-4 mt-6">
            <img
              src={userProfilePic}
              alt="Profile"
              className={`w-20 h-20 rounded-full object-cover ${getSubscriptionBorderColor(registeredData?.user?.subscription?.product?.subscriptionType)}`}
            />
            <div>
              <h2 className="text-lg font-semibold text-primaryBlueText capitalize">
                {userFullName}
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center justify-center text-sm w-fit px-2 h-[34px] rounded-full bg-[#F0F2F3] text-primaryBlueText">

                  <img src={ReviewsRatingIcon} alt="Rating" className="w-5 h-5" />
                  {`${registeredData?.profiles?.host?.avgRating || "No Rating"}`}
                </span>
                <span className="flex items-center justify-center text-sm w-fit px-2 h-[34px] rounded-full bg-[#F0F2F3] text-primaryBlueText">

                  <Award className="mr-1" />
                  {`${registeredData?.user?.subscription?.product?.subscriptionType?.replace(/_/g, ' ') ||
                    "Basic Tier"
                    }`}
                </span>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="flex items-center gap-3 lg:justify-between flex-wrap lg:flex-nowrap bg-[#DE9C2714] rounded-lg lg:rounded-full px-3 py-3 mt-6">
            <span className="flex items-center text-base whitespace-nowrap text-blackie">
              <AwardSecond className="mr-1" />{" "}
              {`${registeredData?.profiles?.host?.badgesEarned || "No Badge"}`}{" "}
            </span>
            <span className="flex items-center text-base whitespace-nowrap text-blackie">
              <ExperienceChart className="mr-1" />{" "}
              {`${registeredData?.profiles?.host?.pastEvents || "No"}`}{" "}
              Experience
            </span>
            <span className="flex items-center whitespace-nowrap text-base text-blackie">
              <SheildTick className="mr-1" />
              {"Since "}
              {registeredData?.profiles?.host?.createdAt
                ? new Date(registeredData.profiles.host.createdAt).getFullYear()
                : "--"}
            </span>
          </div>

          <div className=" flex flex-col ">
            {/* Only visible on screens smaller than md */}
            <div className="md:hidden flex flex-col">
              {[
                {
                  label: "Community",
                  icon: <CommunityIconHost />,
                  link: "/communities",
                },
                // {
                //   label: "Experiences",
                //   icon: <AwardLink />,
                //   link: "/experiences",
                // },
              ].map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleNavigation(item)}
                  className="flex py-5 items-center w-full text-left text-darkBlueText bordr-linear cursor-pointer"
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>

            {/* Other links (visible on all screen sizes) */}
            <div className="flex flex-col">
              {[
                {
                  label: "Edit Profile",
                  icon: <EditTwo />,
                  link: "/edit-profile",
                },
                {
                  label: "My Experiences",
                  icon: <FavoriteChart />,
                  link: "/experiences",
                },
                {
                  label: "Subscription",
                  icon: <AwardLink />,
                  link: "/subscription",
                },
                {
                  label: "Resource Library",
                  icon: <FolderOpen />,
                  link: "/resource-library",
                },
                { label: "My Badges", icon: <Verify />, link: "/my-badges" },
                { label: "Event Badges", icon: <EventsBadgeSvg />, link: "/host-event-badges" },
                {
                  label: "My Cancelled Tickets",
                  icon: <TicketExpiredIcon />,
                  link: "/my-cancelled-tickets",
                },
                {
                  label: "My Representatives",
                  icon: <SecurityUser />,
                  link: "/my-representatives",
                },
                {
                  label: "My Ratings",
                  icon: <QuoteCircle />,
                  link: "/my-ratings",
                },
                {
                  label: "Manage Payments",
                  icon: <ManagePaymentsSvg />,
                  link: "/manage-payment",
                },
                {
                  label: "General Settings",
                  icon: <Setting />,
                  link: "/general-setting",
                },
                {
                  label: "Customer Support",
                  icon: (
                    // You can replace this with a more appropriate icon if needed.
                    <SupportHost />
                  ),
                  link: "#",
                },
                { label: "Logout", icon: <Logout />, link: "/auth/login" },
              ].map((item, index, array) => (
                <div
                  key={index}
                  onClick={() => handleNavigation(item)}
                  className={`flex  items-center w-full text-left text-darkBlueText cursor-pointer ${
                    index === array.length - 1 ? " pt-5" : "bordr-linear py-5"
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Render modals outside the drawer overlay */}
      {showCompleteProfileModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 'calc(var(--z-drawer) + 1)' }}>
          <div className="bg-white p-6 rounded-lg shadow-lg w-[85%] sm:w-[364px] text-center">
            <div className="flex justify-end">
              <button
                onClick={() => setShowCompleteProfileModal(false)}
                className="text-secondaryText text-3xl rounded"
              >
                <IoIosCloseCircleOutline />
              </button>
            </div>
            <div className="flex justify-center">
              <img
                src={profileGif}
                alt="Complete Profile"
                className="w-32 mx-auto mb-2"
              />
            </div>
            <h2 className="text-base text-primaryBlueText font-semibold mb-4">
              Complete your profile
            </h2>
            <p className="text-labelTextColor text-sm mb-4">
              Must complete profile and attest to agreement before posting an
              event.
            </p>
            <button
              onClick={() => {
                navigate(siteRoutes.hostProfile);
                onClose();
              }}
              className="bg-primaryBlueText text-white px-4 py-2 rounded-full"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Customer Support Modal */}

      {showSupportDrawer && (
        <CustomerSupport
          isOpen={showSupportDrawer}
          onClose={() => setShowSupportDrawer(false)}
        />
      )}
    </Fragment>
  );
};

export default NavbarDrawer;
